import { HIGH_PRECISION_DECIMAL_PLACES } from '@/constants';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { Amount } from '@/types/amount';
import { ConversionDetails } from '@/types/transaction';
import { ConversionRates, getConversionRates } from '@/utility/getConversionRates';

const RATE_FORMATTER_CONFIG = {
  maximumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
  minimumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
};

interface FormattedConversionRates {
  readonly client: string;
  readonly market: string;
  readonly effective: string | null;
}

const ONE = 1;
const isOne = (value: number): boolean =>
  value.toFixed(HIGH_PRECISION_DECIMAL_PLACES) === ONE.toFixed(HIGH_PRECISION_DECIMAL_PLACES);

export const useFormattedRates = (
  fromAmount: Amount,
  toAmount: Amount,
  conversionDetails: ConversionDetails | null | undefined,
): FormattedConversionRates => {
  const formatter = useLocalizedFormatter(RATE_FORMATTER_CONFIG);
  const convertedAmount = conversionDetails?.amountToBeConverted;

  if (!convertedAmount) {
    return {
      client: `1 ${fromAmount.currencyCode} = 1 ${toAmount.currencyCode}`,
      market: `1 ${fromAmount.currencyCode} = 1 ${toAmount.currencyCode}`,
      effective: null,
    };
  }

  const rates: ConversionRates | null = getConversionRates(conversionDetails);
  if (!rates || isOne(rates.client)) {
    return {
      client: `1 ${fromAmount.currencyCode} = 1 ${toAmount.currencyCode}`,
      market: `1 ${fromAmount.currencyCode} = 1 ${toAmount.currencyCode}`,
      effective: null,
    };
  }

  const toCurrencyCode = convertedAmount.currencyCode;
  const fromCurrencyCode =
    toCurrencyCode === toAmount.currencyCode ? fromAmount.currencyCode : toAmount.currencyCode;
  const shouldSwap = rates.client < 1;

  const majorCurrency = shouldSwap ? toCurrencyCode : fromCurrencyCode;
  const minorCurrency = shouldSwap ? fromCurrencyCode : toCurrencyCode;

  const getReciprocalIfSwapped = shouldSwap
    ? (value: number): number => (value === 0 ? 0 : 1 / value)
    : (value: number): number => value;

  const getDisplayValue = (value: number): string => {
    return `1 ${majorCurrency} = ${formatter.format(value)} ${minorCurrency}`;
  };

  return {
    client: getDisplayValue(getReciprocalIfSwapped(rates.client)),
    market: getDisplayValue(getReciprocalIfSwapped(rates.market)),
    effective: rates.effective ? getDisplayValue(getReciprocalIfSwapped(rates.effective)) : null,
  };
};
